import { Fragment, useState, useEffect, useMemo } from 'react'
import useUserStore from './UserStore'
import { Dialog, Menu, Transition } from '@headlessui/react'
// Remove useLocation import
import {
  Bars3Icon,
  BellIcon,
  CalendarIcon,
  ChartPieIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  MusicalNoteIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import bandtango_logo from './bandtango.png'
import { fileUploadUrl } from './config'

const initialNavigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon, current: false },
  { name: 'Billboard', href: '/billboard', icon: MusicalNoteIcon, current: false },
  { name: 'Orders', href: '/orders', icon: DocumentDuplicateIcon, current: false },
  { name: 'Invoices', href: '/invoices', icon: CurrencyDollarIcon, current: false },
  { name: 'Customers', href: '/customers', icon: UsersIcon, current: false },
  { name: 'Spots', href: '/spots', icon: FolderIcon, current: false },
  { name: 'Logs', href: '/logs', icon: CalendarIcon, current: false },
  { name: 'Packages', href: '/packages', icon: ChartPieIcon, current: false },
  { name: 'Manual Upload', href: fileUploadUrl, icon: ArrowUpTrayIcon, current: false },
]

const userNavigation = [
  { name: 'Profile', href: '/profile' },
  { name: 'Sign out', href: '/logout' },
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function BackstageSideMenu() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [navigation, setNavigation] = useState([])
  const [currentPath, setCurrentPath] = useState(window.location.pathname)

  const storeUser = useUserStore((state) => state.user)
    
  const username = storeUser?.authResponse?.profile?.username || storeUser?.username || 'User';
  const email = storeUser?.email || storeUser?.authResponse?.email || 'user@example.com';
  const displayName = storeUser?.authResponse?.profile?.first_name && storeUser?.authResponse?.profile?.last_name
    ? `${storeUser.authResponse.profile.first_name} ${storeUser.authResponse.profile.last_name}`
    : username;

  const avatarUrl = storeUser?.authResponse?.profile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random`;  

  // Derive allowed nav items from functional_areas stored in the user profile.
  // Falls back to showing all items when no functional_areas are present.
  const allowedNavigation = useMemo(() => {
    const areas = storeUser?.authResponse?.profile?.functional_areas
    if (!areas || !Array.isArray(areas) || areas.length === 0) {
      return initialNavigation
    }
    const lower = areas.map((a) => a.toLowerCase())
    return initialNavigation.filter((item) => lower.includes(item.name.toLowerCase()))
  }, [storeUser])

  const isAdminArea = useMemo(() => {
    const areas = storeUser?.authResponse?.profile?.functional_areas
    if (!areas || !Array.isArray(areas)) return false
    return areas.includes('Admin')
  }, [storeUser])

  // Function to update navigation state based on current URL
  const updateNavigationFromURL = (pathname) => {
    console.log('Updating navigation for pathname:', pathname)
    
    const updatedNavigation = allowedNavigation.map(item => {
      let isCurrent = false
      
      if (pathname === '/' && item.href === '/') {
        // Exact match for home page
        isCurrent = true
      } else if (pathname !== '/' && item.href !== '/' && pathname.startsWith(item.href)) {
        // Partial match for other pages (e.g., /customers/123 matches /customers)
        isCurrent = true
      }
            
      return {
        ...item,
        current: isCurrent
      }
    })

    setNavigation(updatedNavigation)
  }

  // Re-filter navigation whenever allowed items change (e.g. after login)
  useEffect(() => {
    updateNavigationFromURL(window.location.pathname)
  }, [allowedNavigation])

  // Listen for URL changes (for SPA navigation)
  useEffect(() => {
    const handleLocationChange = () => {
      const newPath = window.location.pathname;
      setCurrentPath(newPath);
      updateNavigationFromURL(newPath);
    };

    // Initial load
    updateNavigationFromURL(window.location.pathname);

    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', handleLocationChange);

    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      handleLocationChange();
    };

    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      handleLocationChange();
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  // Function to handle navigation item clicks
  const handleNavClick = (clickedItemName, href) => {
    // For internal links, update state immediately (for instant feedback)
    const updatedNavigation = allowedNavigation.map(item => ({
      ...item,
      current: item.name === clickedItemName
    }));

    setNavigation(updatedNavigation);
  }

  return (
    <div>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  {/* Mobile Sidebar component */}
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                    <div className="flex h-16 shrink-0 items-center">
                      <img className="h-12" src={bandtango_logo} alt="BANDTANGO"/>
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul className="-mx-2 space-y-1">
                            {navigation.map((item) => (
                              <li key={item.name}>
                                <a
                                  href={item.href}
                                  onClick={(e) => {
                                    handleNavClick(item.name, item.href);
                                  }}
                                  className={classNames(
                                    item.current
                                      ? 'bg-gray-200 text-indigo-600'
                                      : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-100',
                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                                      'h-6 w-6 shrink-0'
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </li>
                        {isAdminArea && (
                        <li className="mt-auto">
                          <a
                            href="/admin"
                            onClick={() => {
                              console.log('Admin clicked (mobile)');
                            }}
                            className={classNames(
                              currentPath === '/admin'
                                ? 'bg-gray-200 text-indigo-600'
                                : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-100',
                              'group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6'
                            )}
                          >
                            <Cog6ToothIcon
                              className={classNames(
                                currentPath === '/admin' ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                                'h-6 w-6 shrink-0'
                              )}
                              aria-hidden="true"
                            />
                            Admin
                          </a>
                        </li>
                        )}
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center">
              <img className="h-12" src={bandtango_logo} alt="BANDTANGO"/>
            </div>
            <nav className="flex flex-1 flex-col">
              <ul className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <a
                          href={item.href}
                          onClick={(e) => {
                            handleNavClick(item.name, item.href);
                          }}
                          className={classNames(
                            item.current
                              ? 'bg-gray-200 text-indigo-600'
                              : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-100',
                            'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                              'h-6 w-6 shrink-0'
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
                {isAdminArea && (
                <li className="mt-auto">
                  <a
                    href="/admin"
                    onClick={() => {
                      console.log('Admin clicked (desktop)');
                    }}
                    className={classNames(
                      currentPath === '/admin'
                        ? 'bg-gray-200 text-indigo-600'
                        : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-100',
                      'group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6'
                    )}
                  >
                    <Cog6ToothIcon
                      className={classNames(
                        currentPath === '/admin' ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                        'h-6 w-6 shrink-0'
                      )}
                      aria-hidden="true"
                    />
                    Admin
                  </a>
                </li>
                )}
              </ul>
            </nav>
          </div>
        </div>

        {/* Rest of your component remains the same */}
        <div className="lg:pl-72">
          <div className="z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <button type="button" className="-m-2.5 p-2.5 text-gray-700 lg:hidden" onClick={() => setSidebarOpen(true)}>
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Separator */}
            <div className="h-6 w-px bg-gray-200 lg:hidden" aria-hidden="true" />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <form className="relative flex flex-1" action="#" method="GET">
                <label htmlFor="search-field" className="sr-only">
                  Search
                </label>
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  id="search-field"
                  className="block h-full w-full border-0 py-0 pl-10 pr-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                  placeholder="Search..."
                  type="search"
                  name="search"
                />
              </form>
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <a href="/notifications" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                  <span className="sr-only">View notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                </a>

                {/* Separator */}
                <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" aria-hidden="true" />

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <Menu.Button className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>

                    <img
                      className="h-8 w-8 rounded-full bg-gray-50"
                      src={avatarUrl}
                      alt=""
                    />
                    <span className="hidden lg:flex lg:items-center">
                      <span className="ml-4 text-sm font-semibold leading-6 text-gray-900" aria-hidden="true">
                        {displayName}
                      </span>
                      <span className="ml-2 hidden flex-none text-gray-400 lg:block" aria-hidden="true">
                        {email}
                      </span>
                      <span className="ml-2 h-5 w-5 flex-none text-gray-400" aria-hidden="true">
                      </span>
                      <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      {userNavigation.map((item) => (
                        <Menu.Item key={item.name}>
                          {({ active }) => (
                            <a
                              href={item.href}
                              className={classNames(
                                active ? 'bg-gray-50' : '',
                                'block px-3 py-1 text-sm leading-6 text-gray-900'
                              )}
                            >
                              {item.name}
                            </a>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}